import { useState, useEffect } from "react";
import { AlertCircle, CheckCircle, Loader2, Clock, AlertTriangle } from "lucide-react";
import { ParsedPdf } from "../utils/parse-zip";
import { renderPdfToCanvases } from "../utils/render-pdf";
import { comparePdfPages, PdfComparison } from "../utils/compare-pdfs";
import { PdfComparisonCard } from "./pdf-comparison-card.component";
import { PdfPageViewerModal } from "./pdf-page-viewer-modal.component";

interface PdfComparisonResultsProps {
	zip1Pdfs: ParsedPdf[];
	zip2Pdfs: ParsedPdf[];
}

export const PdfComparisonResults: React.FC<PdfComparisonResultsProps> = ({
	zip1Pdfs,
	zip2Pdfs,
}) => {
	const [comparisons, setComparisons] = useState<PdfComparison[]>([]);
	const [isComparing, setIsComparing] = useState(false);
	const [progress, setProgress] = useState<{ current: number; total: number; estimatedRemainingMs?: number }>({ current: 0, total: 0 });
	const [error, setError] = useState<string | null>(null);
	const [totalTimeMs, setTotalTimeMs] = useState<number>(0);
	// Review state: undefined = not reviewed, true = okay, false = problematic
	const [reviewState, setReviewState] = useState<Map<string, boolean | undefined>>(new Map());
	const [viewerModalOpen, setViewerModalOpen] = useState(false);
	const [selectedComparisonIndex, setSelectedComparisonIndex] = useState(0);

	const getComparisonKey = (comparison: PdfComparison) =>
		`${comparison.orgId}::${comparison.clientName}::${comparison.pdfType}::${comparison.settingsPart}`;

	const updateReview = (comparison: PdfComparison, status: boolean | undefined) => {
		const key = getComparisonKey(comparison);
		setReviewState(prev => {
			const next = new Map(prev);
			next.set(key, status);
			return next;
		});
	};

	const handleOpenViewer = (comparison: PdfComparison) => {
		const index = comparisons.indexOf(comparison);
		if (index >= 0) {
			setSelectedComparisonIndex(index);
			setViewerModalOpen(true);
		}
	};

	useEffect(() => {
		compareAllPdfs();
	}, [zip1Pdfs, zip2Pdfs]);

	const compareAllPdfs = async () => {
		const startTime = performance.now();
		try {
			setIsComparing(true);
			setError(null);
			setComparisons([]);

			// Create a map of PDFs from zip2 for easy lookup
			const zip2Map = new Map<string, ParsedPdf>();
			zip2Pdfs.forEach(pdf => {
				const key = `${pdf.orgId}::${pdf.clientName}::${pdf.pdfType}::${pdf.settingsPart}`;
				zip2Map.set(key, pdf);
			});

			// Find all unique combinations
			const allKeys = new Set([
				...zip1Pdfs.map(pdf => `${pdf.orgId}::${pdf.clientName}::${pdf.pdfType}::${pdf.settingsPart}`),
				...zip2Pdfs.map(pdf => `${pdf.orgId}::${pdf.clientName}::${pdf.pdfType}::${pdf.settingsPart}`),
			]);

			setProgress({ current: 0, total: allKeys.size });

			const newComparisons: PdfComparison[] = [];
			let current = 0;
			const iterationTimes: number[] = [];
			let lastIterationTime = performance.now();

			for (const key of allKeys) {
				const [orgId, clientName, pdfType, ...settingsPartArray] = key.split('::');
				const settingsPart = settingsPartArray.join('::'); // Rejoin in case settings contain '::'
				const pdf1 = zip1Pdfs.find(p =>
					p.orgId === orgId &&
					p.clientName === clientName &&
					p.pdfType === pdfType &&
					p.settingsPart === settingsPart
				);
				const pdf2 = zip2Map.get(key);

				if (!pdf1 && !pdf2) continue;

				try {
					// Render both PDFs
					const pages1 = pdf1 ? await renderPdfToCanvases(pdf1.blob) : [];
					const pages2 = pdf2 ? await renderPdfToCanvases(pdf2.blob) : [];

					// Compare them
					const comparison = comparePdfPages(pages1, pages2, orgId, pdfType, clientName, settingsPart);
					newComparisons.push(comparison);
				} catch (err) {
					console.error(`Error comparing ${orgId}/${clientName}/${pdfType}:`, err);
					setError(`Fehler beim Vergleichen von ${orgId}/${clientName}/${pdfType}: ${err instanceof Error ? err.message : 'Unbekannter Fehler'}`);
				}

				current++;

				// Track iteration time for moving average
				const currentTime = performance.now();
				const iterationTime = currentTime - lastIterationTime;
				lastIterationTime = currentTime;

				iterationTimes.push(iterationTime);
				if (iterationTimes.length > 10) {
					iterationTimes.shift();
				}

				// Calculate moving average of last 10 iterations
				const avgTimePerComparison = iterationTimes.reduce((sum, time) => sum + time, 0) / iterationTimes.length;
				const remainingComparisons = allKeys.size - current;
				const estimatedRemainingMs = avgTimePerComparison * remainingComparisons;

				setProgress({ current, total: allKeys.size, estimatedRemainingMs });
			}

			setComparisons(newComparisons);

			const endTime = performance.now();
			setTotalTimeMs(endTime - startTime);
		} catch (err) {
			setError(`Fehler beim Vergleichen: ${err instanceof Error ? err.message : 'Unbekannter Fehler'}`);
		} finally {
			setIsComparing(false);
		}
	};

	const formatRemainingTime = (ms: number) => {
		const totalSeconds = Math.ceil(ms / 1000);
		const minutes = Math.floor(totalSeconds / 60);
		const seconds = totalSeconds % 60;
		return `${minutes}:${String(seconds).padStart(2, '0')}`;
	};

	if (isComparing) {
		return (
			<div className="bg-white border border-gray-200 rounded-lg p-8">
				<div className="flex flex-col items-center justify-center gap-4">
					<Loader2 className="w-12 h-12 animate-spin text-blue-600" />
					<div className="text-center">
						<h3 className="font-semibold text-gray-900 mb-1">
							PDFs werden verglichen...
						</h3>
						<p className="text-sm text-gray-600">
							{progress.current} von {progress.total} abgeschlossen
						</p>
						{progress.estimatedRemainingMs !== undefined && (
							<p className="text-xs text-gray-500 mt-1">
								ca. {formatRemainingTime(progress.estimatedRemainingMs)} verbleibend
							</p>
						)}
					</div>
				</div>
			</div>
		);
	}

	if (error) {
		return (
			<div className="bg-red-100 border-2 border-red-600 rounded-lg p-4">
				<div className="flex items-center gap-2">
					<AlertCircle className="w-5 h-5 text-red-600" />
					<p className="text-sm text-red-900">{error}</p>
				</div>
			</div>
		);
	}

	const comparisonsWithDiff = comparisons.filter(c => c.hasAnyDifference);
	const comparisonsWithoutDiff = comparisons.filter(c => !c.hasAnyDifference);

	// Get problematic PDFs (marked with false)
	const problematicComparisons = comparisons.filter(c => {
		const key = getComparisonKey(c);
		return reviewState.get(key) === false;
	});

	const avgTimeMs = comparisons.length > 0 ? totalTimeMs / comparisons.length : 0;
	const formatTime = (ms: number) => {
		if (ms < 1000) return `${ms.toFixed(0)}ms`;
		return `${(ms / 1000).toFixed(2)}s`;
	};

	return (
		<div className="space-y-6">
			<div className="bg-white border border-gray-200 rounded-lg p-6">
				<h2 className="text-lg font-semibold text-gray-900 mb-4">
					Vergleichsergebnisse
				</h2>

				<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
					<div className="bg-blue-100 border-2 border-blue-600 rounded p-4">
						<div className="text-2xl font-bold text-blue-950">
							{comparisons.length}
						</div>
						<div className="text-sm text-blue-900">
							Gesamt verglichen
						</div>
					</div>

					<div className="bg-red-100 border-2 border-red-600 rounded p-4">
						<div className="flex items-center gap-2">
							<AlertCircle className="w-5 h-5 text-red-600" />
							<div className="text-2xl font-bold text-red-950">
								{comparisonsWithDiff.length}
							</div>
						</div>
						<div className="text-sm text-red-900">
							Mit Unterschieden
						</div>
					</div>

					<div className="bg-green-100 border-2 border-green-600 rounded p-4">
						<div className="flex items-center gap-2">
							<CheckCircle className="w-5 h-5 text-green-600" />
							<div className="text-2xl font-bold text-green-950">
								{comparisonsWithoutDiff.length}
							</div>
						</div>
						<div className="text-sm text-green-900">
							Identisch
						</div>
					</div>

					<div className="bg-purple-100 border-2 border-purple-600 rounded p-4">
						<div className="flex items-center gap-2">
							<Clock className="w-5 h-5 text-purple-600" />
							<div className="text-2xl font-bold text-purple-950">
								{formatTime(totalTimeMs)}
							</div>
						</div>
						<div className="text-sm text-purple-900">
							Gesamtzeit
						</div>
					</div>

					<div className="bg-indigo-100 border-2 border-indigo-600 rounded p-4">
						<div className="flex items-center gap-2">
							<Clock className="w-5 h-5 text-indigo-600" />
							<div className="text-2xl font-bold text-indigo-950">
								{formatTime(avgTimeMs)}
							</div>
						</div>
						<div className="text-sm text-indigo-900">
							Ø pro Dokument
						</div>
					</div>
				</div>
			</div>

			{/* Problematic PDFs Summary */}
			{problematicComparisons.length > 0 && (
				<div className="bg-orange-100 border-2 border-orange-600 rounded-lg p-6">
					<div className="flex items-center gap-3 mb-4">
						<AlertTriangle className="w-6 h-6 text-orange-600" />
						<h2 className="text-lg font-semibold text-orange-950">
							{problematicComparisons.length} Problematische PDF{problematicComparisons.length !== 1 ? 's' : ''} gefunden
						</h2>
					</div>
					<p className="text-sm text-orange-950 mb-4">
						Die folgenden PDFs wurden als problematisch markiert und benötigen Ihre Aufmerksamkeit:
					</p>
					<div className="grid grid-cols-1 md:grid-cols-2 gap-3">
						{problematicComparisons.map((comparison, idx) => {
							const totalDiffPercentage = comparison.pages.reduce(
								(sum, page) => sum + page.diffPercentage,
								0
							) / comparison.pages.length;

							return (
								<div
									key={`problematic-${idx}`}
									className="bg-white border border-orange-300 rounded p-4 cursor-pointer hover:bg-orange-50 transition-colors"
									onClick={() => handleOpenViewer(comparison)}
								>
									<div className="flex items-center justify-between">
										<div>
											<h3 className="font-semibold text-gray-900">
												{comparison.orgId}
											</h3>
											<p className="text-sm text-gray-600">
												{comparison.clientName}
											</p>
											<p className="text-xs text-gray-500">
												{comparison.pdfType}
											</p>
										</div>
										<div className="text-right">
											<div className="text-lg font-bold text-orange-950">
												{totalDiffPercentage.toFixed(2)}%
											</div>
											<div className="text-xs text-orange-900">
												unterschiedlich
											</div>
										</div>
									</div>
								</div>
							);
						})}
					</div>
				</div>
			)}

			{comparisonsWithDiff.length > 0 && (
				<div>
					<h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
						<AlertCircle className="w-5 h-5 text-red-600" />
						PDFs mit Unterschieden ({comparisonsWithDiff.length})
					</h3>
					<div className="space-y-4">
						{comparisonsWithDiff.map((comparison, idx) => (
							<PdfComparisonCard
								key={`${comparison.orgId}-${comparison.clientName}-${comparison.pdfType}-${idx}`}
								comparison={comparison}
								allComparisons={comparisons}
								comparisonIndex={comparisons.indexOf(comparison)}
								reviewState={reviewState}
								onUpdateReview={updateReview}
							/>
						))}
					</div>
				</div>
			)}

			{comparisonsWithoutDiff.length > 0 && (
				<div>
					<h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
						<CheckCircle className="w-5 h-5 text-green-600" />
						Identische PDFs ({comparisonsWithoutDiff.length})
					</h3>
					<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
						{comparisonsWithoutDiff.map((comparison, idx) => (
							<div
								key={`${comparison.orgId}-${comparison.clientName}-${comparison.pdfType}-${idx}`}
								className="bg-green-100 border-2 border-green-600 rounded p-3"
							>
								<div className="text-sm font-medium text-green-950">
									{comparison.orgId}
								</div>
								<div className="text-xs text-green-900">
									{comparison.clientName}
								</div>
								<div className="text-xs text-green-800">
									{comparison.pdfType} ({comparison.maxPages} Seite{comparison.maxPages !== 1 ? 'n' : ''})
								</div>
							</div>
						))}
					</div>
				</div>
			)}

			<PdfPageViewerModal
				comparisons={comparisons}
				initialComparisonIndex={selectedComparisonIndex}
				initialPageIndex={0}
				isOpen={viewerModalOpen}
				onClose={() => setViewerModalOpen(false)}
				reviewState={reviewState}
				onUpdateReview={updateReview}
			/>
		</div>
	);
};
